// CallRouteGuide.js
// ─────────────────────────────────────────────────────────────────────────
// Makes the LIVE agent route-aware. While a global call is running (Android/
// iOS only), every time the user navigates to a new screen we inject a short
// context note into the call via Retell's update-live-call API. The agent
// reads that note and guides the user on THIS page — e.g. on the Go-Live
// payment screen it ASKS if they're ready to go laiv (the pay button is hidden
// until confirm_go_live), and only invites them to tap it after they agree.
//
// Mounted once at the App root (inside the Router, next to RouteTracker /
// GlobalCallBanner). Renders nothing.
// ─────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useGlobalCall,
  isMobileAppSurface,
} from "../../contexts/GlobalCallContext";
import { useOfferStore } from "../../Store/offerStore";
import { FREE_MINUTES, currentPaygPricing, detectPaygPricing } from "../../lib/paygPricing";

// Look the visitor's country up as soon as the app loads, so the PAYG guide
// below has the right currency by the time a call reaches the pay screen.
detectPaygPricing();

// Per-route guidance for the agent. Keys are matched exactly first, then by
// path prefix. Phrase each value as a CONTEXT UPDATE + a one-line instruction
// so the agent speaks briefly and doesn't interrogate the user.
// Add more screens here as the guided-call flow grows.
// Prepended to EVERY route guide. Global rules only — NO intro rules here (the
// intro is handled per-route: ONLY /fast-step introduces; every other screen
// gets a strong NO-INTRO note via matchGuide()).
  // "GUIDED CALL — GLOBAL RULES:\n" +
  // "1) Keep EVERY reply SHORT and clear — around 20 words in most cases, 40 words MAXIMUM only if the query is complex and genuinly requires a longer explaination.\n" +
  // "2) Be warm and natural. Never read a long list unless the customer explicitly asks.\n" +
  // "3) If the customer asks a question, give a brief answer — at most ONE or TWO answers — then come STRAIGHT to what's relevant on this screen. Do NOT drag the conversation: after one or two answers, always guide them to this screen's point. Answer first, never ignore a question, but keep it tight and transition quickly to the screen's subject.\n\n";
const GUIDE_PREFIX =
  "[SYSTEM INSTRUCTION — this is guidance FOR YOU about the screen the customer is now on. Do NOT read any of it aloud, do NOT repeat or narrate it. Just act on it naturally in the conversation.]\n" +
  "SETUP-CALL RULES — FOLLOW STRICTLY:\n" +
  "• SHORT replies only: 1–2 sentences, ~20 words (40 max only if truly needed).only if the query is complex and genuinly requires a longer explaination.\n" +
  "• Answer AT MOST TWO questions. Right after the second answer, IMMEDIATELY say this screen's point (the TASK below) — do NOT keep chatting or wait.\n" +
  "• After ANY answer, end by steering back to this screen's point. Never ignore a question, but never drift from the screen either.\n" +
  "• Warm and natural. No long lists unless asked.\n\n";

// The screen where the agent introduces itself. Everywhere else = no intro.
const INTRO_ROUTE = "/fast-step";

// Prepended on every screen EXCEPT the intro screen. Emphatic so the agent's
// built-in greeting habit doesn't make it re-introduce on later screens.
const NO_INTRO_NOTE =
  "DO NOT INTRODUCE YOURSELF ON THIS SCREEN. You already greeted the customer earlier in this call. Do NOT say your name, do NOT say hi/hello, do NOT greet — go STRAIGHT to the point below.\n\n";

// /recommended-plan shows ONE of two offers on the same URL (see GoLiveVariant):
// the $5/mo PAYG page or the 21-day trial. The live agent must guide the one
// that's actually on screen, so we read the active variant at inject time.
// The numbers follow the visitor's pricing (USD, or CAD for Canada); the
// wording is the same for everyone.
const recommendedPaygGuide = () => {
  const p = currentPaygPricing();
  const f = `$${p.fee}`;
  const r = `$${p.rate.toFixed(2)}`;
  const free = FREE_MINUTES;
  const cost = (n) => `$${Math.round(p.fee + Math.max(0, n - free) * p.rate)}`;
  const cad = p.currency === "cad" ? " All prices are in Canadian dollars (CAD)." : "";
  return (
    `SCREEN: 'Go Laiv for ${f}/month'. Facts you can use if asked:\n` +
    `- ${f}/month. First ${free} call minutes FREE. After that ${f}/month + ${r} per minute.${cad}\n` +
    "- Only charged for REAL answered calls. Spam blocked free — never billed for junk.\n" +
    "- Included: own local business number; 24/7 natural-voice answering; automatic calendar booking; call triage & routing with their rules; spam blocking.\n" +
    "- A 'Calculate my monthly cost' button opens a cost breakdown. Higher plans ($119–$599/month) exist if they need more.\n" +
    `- COST: ${f}/month includes ONLY ${free} minutes, then ${r} per extra minute. Formula: ${f} + (minutes − ${free}) × ${r}. Example: 150 minutes = ${cost(150)}/month; 500 minutes = ${cost(500)}/month. NEVER say heavy usage like 500 minutes is still ${f} — always COMPUTE with the formula.\n` +
    `- The large 'Go Laiv For ${f}/Mo' button opens secure Stripe checkout; cancel anytime.\n` +
    `TASK: In one short sentence tell them they can go laiv for ${f}/month with the first ${free} minutes free, and ASK if they're ready to go laiv. IMPORTANT: the 'Go Laiv For ${f}/Mo' button is HIDDEN right now — do NOT tell them to tap any button yet. Only when they clearly say yes, call confirm_go_live (that reveals the button), then invite them to tap it. If they ask about pricing, the calculator, or features, answer briefly from the facts above.`
  );
};

const RECOMMENDED_TRIAL_GUIDE =
  "SCREEN: 'Start your 21-Day trial'. Facts you can use if asked:\n" +
  "- A 100% risk-free 21-DAY trial. It includes 150 minutes of real AI call answering (valued $72).\n" +
  "- Today they pay just $20.\n" +
  "- After 21 days they pick a plan ($119–$599/month), or stay on the Starter plan ($119/month).\n" +
  "- We remind them 2 days before any charge — no surprises.\n" +
  "- Includes: premium multilingual receptionist; book jobs directly in Calendar or CRM; active spam filtering.\n" +
  "- The 'Start your 21-Day trial' button goes to secure checkout. A 'Plans $119–$599/mo' link shows the plan options.\n" +
  "TASK: In one short sentence tell them they can start a 21-day trial for just $20 today — 150 minutes included — and ASK if they're ready to go laiv. IMPORTANT: the 'Start your 21-Day trial' button is HIDDEN right now — do NOT tell them to tap any button yet. Only when they clearly say yes, call confirm_go_live (that reveals the button), then invite them to tap it. If they ask about pricing or what's included, answer briefly from the facts above.";

// Pick the guide for the offer currently on screen (passed in from context).
const recommendedPlanGuide = (variant) =>
  variant === "trial" ? RECOMMENDED_TRIAL_GUIDE : recommendedPaygGuide();

const ROUTE_GUIDE = {
  // COMMENTED OUT: no update-live-call inject on the /fast-step "Meet Sophia"
  // popup — the agent greets naturally from its own prompt there. Injection
  // only starts on later screens. Uncomment to re-enable /fast-step guidance.
  /*
  "/fast-step":
    "SCREEN: 'Your receptionist is ready' (right after you were built).\n" +
    "FIRST, greet with a ONE-line intro — your name and the business you answer for (e.g. \"Hi, I'm <your name> for <business name> Go ahead and try me out like a real customer.\"). This is the ONLY screen where you introduce yourself.\n" +
    "Facts you can use if asked:\n" +
    "- It says you're ready for their business, with a 'Test again' button to hear you.\n" +
    "- What you just did: answered in 1.2 seconds (no voicemail, no lost customer); ready to book appointments into their calendar; block spam free — they never pay for junk.\n" +
    "- A traditional full-time receptionist costs $3,500+/month; you work 24/7 for far less. Every call you catch is a typical $250 customer they'd have missed.\n" +
    "- The big 'Go Laiv' button at the bottom takes them to the $5/month plan.\n" +
    "- PRICING (if they ask): $5/month includes ONLY 50 minutes; beyond that it's $0.48 per minute.\n" +
    "  COST FORMULA for N expected minutes per month:\n" +
    "     cost = $5 + (N − 50) × $0.48   (if N is 50 or less, the cost is just $5).\n" +
    "     Example: N = 150 → $5 + (150 − 50) × $0.48 = $5 + $48 = $53 per month.\n" +
    "  IMPORTANT: $5 covers only 50 minutes. NEVER say heavy usage (like 500 minutes) is still $5 — that is wrong. For 500 minutes it is $5 + (450 × $0.48) = $221/month. Always COMPUTE with the formula.\n" +
    "TASK: After your one-line intro, in one short sentence tell them you're ready to work 24/7 and to tap 'Go Laiv' when ready. If they ask about price, use the formula.",
  */
  // Function → resolved at inject time to the on-screen offer ($5/mo or trial).
  "/recommended-plan": recommendedPlanGuide,
  "/receptionist-number":
    "SCREEN: 'Your Receptionist\\'s Number' page where their new local phone number is shown. TASK: In ONE short, warm sentence, tell them this is the business number picked for them, and invite them to tap 'Continue with this number' to lock it in. Do NOT end the call.",
  "/go-live-setup":
    "SCREEN: setup page after starting Go Laiv. TASK: In one short sentence tell them you're getting their business number and settings ready, and to follow the on-screen steps.",
  "/assign-number":
    "SCREEN: business-number assignment page. TASK: In one short sentence tell them this is the local number customers will call, and to tap continue to set up call forwarding.",
};

const matchGuide = (pathname, variant) => {
  let matchedKey = ROUTE_GUIDE[pathname] ? pathname : null;
  if (!matchedKey) {
    matchedKey = Object.keys(ROUTE_GUIDE).find((k) => pathname.startsWith(k)) || null;
  }
  if (!matchedKey) return null;

  // Intro screen introduces; every other screen gets the strong no-intro note.
  const introRule = matchedKey === INTRO_ROUTE ? "" : NO_INTRO_NOTE;
  // Guides may be a static string or a function resolved at inject time
  // (e.g. /recommended-plan picks $5/mo vs 21-day-trial by active variant).
  const raw = ROUTE_GUIDE[matchedKey];
  const guide = typeof raw === "function" ? raw(variant) : raw;
  return GUIDE_PREFIX + introRule + guide;
};

// The live call is only allowed to run on these screens. Navigating anywhere
// else while a call is live ends it (it must not keep running invisibly).
//
// /receptionist-number = A/B variant B ka number screen. Wahan call chalti
// rehni chahiye (GlobalCallBanner uska UI hai), bilkul /recommended-plan ki
// tarah — warna user "Go Live" dabate hi call kat jaati hai.
//
// /agent-live JAAN-BOOJH KE bahar hai: wahan user ko apne PHONE se agent ko
// call karna hota hai, to web call ka chalte rehna ulta hai. Us screen pe
// pahunchte hi ye guard call band kar deta hai — bilkul wahi chahiye.
const CALL_ALLOWED_ROUTES = [
  "/fast-step",
  "/recommended-plan",
  "/receptionist-number",
];
const onCallRoute = (pathname) =>
  CALL_ALLOWED_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(`${r}/`)
  );

const CallRouteGuide = () => {
  const location = useLocation();
  const { callStatus, injectContext, goLiveVariant, endGlobalCall } = useGlobalCall();
  // When the user asked the agent to go quiet (silent_mode tool), we must NOT
  // inject route guidance — injectContext triggers an agent response, which
  // would force the silent agent to speak on navigation.
  const silenced = useOfferStore((s) => s.silenced);
  const clearSilent = useOfferStore((s) => s.clearSilent);
  /*
   * Har Plan_recommendation push par offer store `nonce` badha deta hai — wahi
   * "ye ek NAYA offer push hai" ka signal hai, chahe tier wahi ho.
   *
   * Ise dedupe key me shaamil karna zaroori hai. Pehle key sirf
   * pathname + variant thi, isliye ek hi call me trial screen DOOSRI baar aane
   * par key wahi banti thi aur guide dobara inject hota hi nahi tha: screen to
   * dikh jati thi, par agent ko 21-day trial ka context milta hi nahi tha — wo
   * purani baat karta rehta tha. (GlobalOfferModal isi wajah se pehle se nonce
   * use karta hai.)
   */
  const offerNonce = useOfferStore((s) => s.nonce);
  const forcedOffer = useOfferStore((s) => s.forcedOffer);

  /*
   * Nonce SIRF page-level offers (trial / payg) ke liye track karo.
   *
   * `nonce` har setOffer par badhta hai — "plans" par bhi, jo sirf popup
   * kholta hai aur is page ka guide badalta hi nahi. Agar seedha `nonce`
   * dedupe key me daal dein, to /recommended-plan par plans popup khulte hi
   * key badal jati hai aur neeche wala trial/payg guide DOBARA inject ho jata
   * hai — popup ke apne inject ke upar. Yaani do inject.
   */
  const [pageOfferNonce, setPageOfferNonce] = useState(0);
  useEffect(() => {
    if (forcedOffer === "trial" || forcedOffer === "payg") {
      setPageOfferNonce(offerNonce);
    }
  }, [forcedOffer, offerNonce]);
  // Dedupe key we last injected for (pathname, plus variant + offer nonce on
  // /recommended-plan), so a re-render / revisit doesn't re-inject the same
  // thing — but a genuinely NEW offer push does.
  const lastInjectedRef = useRef(null);

  // Reset the dedupe latch when the call isn't active, so a fresh call re-guides.
  // Also clear silent mode once the call is fully idle so the NEXT call starts
  // guiding normally instead of staying muted.
  useEffect(() => {
    if (callStatus !== "active") lastInjectedRef.current = null;
    if (callStatus === "idle" && silenced) clearSilent();
  }, [callStatus, silenced, clearSilent]);

  // END the live call the moment the user navigates OFF the call screens
  // (/fast-step, /recommended-plan) — e.g. to /fast-agent-detail or the
  // dashboard. The call must not keep running invisibly in the background.
  useEffect(() => {
    if (!isMobileAppSurface()) return;
    const live = callStatus === "active" || callStatus === "connecting";
    if (!live) return;
    if (!onCallRoute(location.pathname)) {
      try {
        endGlobalCall();
      } catch (_e) {
        /* best-effort */
      }
    }
  }, [location.pathname, callStatus, endGlobalCall]);

  useEffect(() => {
    if (!isMobileAppSurface()) return;
    // Gate on ACTIVE (not merely "connecting"): only then is the call_id
    // captured, so the inject can't silently no-op. This matters on /fast-step
    // where the call is still starting up as the screen mounts.
    if (callStatus !== "active") return;
    // Agent was asked to go quiet → do NOT inject (injecting forces it to
    // speak). Stay muted until the user re-engages or the call ends.
    if (silenced) return;

    const { pathname } = location;
    const isRecoPlan = pathname === "/recommended-plan";

    // /recommended-plan shows either $5/mo or the 21-day trial. Wait until
    // GoLiveVariant has decided (goLiveVariant set) so we never inject the
    // WRONG offer's guidance (the bug: screen $5 but agent said $20 trial).
    if (isRecoPlan && !goLiveVariant) return;

    // Dedupe includes the variant AND the offer nonce, so the guide re-injects
    // both when the offer changes (trial ↔ payg) and when the SAME offer is
    // pushed again by a fresh Plan_recommendation call.
    const dedupeKey = pathname + (isRecoPlan ? `:${goLiveVariant}:${pageOfferNonce}` : "");
    if (lastInjectedRef.current === dedupeKey) return;

    const guide = matchGuide(pathname, goLiveVariant);
    if (!guide) return;

    lastInjectedRef.current = dedupeKey;
    injectContext(guide);
  }, [location.pathname, callStatus, injectContext, goLiveVariant, location, silenced, pageOfferNonce]);

  return null;
};

export default CallRouteGuide;
